import classNames from "classnames";
import React, { useMemo } from "react";
import * as Ons from "react-onsenui";
import { DraftPatientContractDiagnosis, PatientContractDiagnosisCode } from "../../../../messages";
import { STRINGS } from "../../../../strings";
import { globalize, translate } from "../../../../translate";
import { fmap } from "../../../../utils/fmap";
import { CALENDAR_SVG, CHECK_SVG, LOCK_SVG, TIMES_SVG } from "../../../../utils/svgs";
import { withDefault } from "../../../../utils/withDefault";
import { CardButton } from "../../../CardButton/CardButton";
import {
  AppCard,
  AppCardContent,
  AppCardFooter,
  AppCardHeader,
  AppCardTitle,
} from "../../../Generic/AppCard";
import { AppIcon } from "../../../Generic/AppIcon";
import AppSwitch from "../../../Generic/AppSwitch/AppSwitch";
import Flex from "../../../Generic/Flex";
import { If } from "../../../Generic/If";
import { IsDefined } from "../../../Generic/IsDefined";

interface Props {
  title: string;
  diagnosis: PatientContractDiagnosisCode | null;
  draft: DraftPatientContractDiagnosis | null;
  isProfileSync: boolean;
  isDisabled: boolean;
  isLoading: boolean;
  onClickCode: () => void;
  onClickStartDate: () => void;
  onClickEndDate: () => void;
  onClickDelete: () => void;
  onClickSave: () => void;
  onClickCancel: () => void;
  onClickProfileSync: (code: PatientContractDiagnosisCode | null) => void;
}

const ICDCard = ({
  title,
  diagnosis: originalDiagnosis,
  draft,
  isProfileSync,
  isDisabled,
  isLoading,
  onClickCode,
  onClickStartDate,
  onClickEndDate,
  onClickDelete,
  onClickSave,
  onClickCancel,
  onClickProfileSync,
}: Props) => {
  const diagnosis = useMemo(() => {
    let _return: DraftPatientContractDiagnosis | null = null;

    if (originalDiagnosis !== null) {
      _return = {
        icdCode: originalDiagnosis.icdCode,
        formattedDxCode: originalDiagnosis.formattedDxCode,
        shortDesc: originalDiagnosis.shortDesc,
        startDate: originalDiagnosis.startDate,
        endDate: originalDiagnosis.endDate,
        type: originalDiagnosis.type,
      };
    }

    if (draft !== null) {
      _return = { ..._return, ...draft };
    }

    return _return;
  }, [originalDiagnosis, draft]);

  return (
    <AppCard className={classNames("ICDCard", { __disabled: isDisabled })}>
      <AppCardHeader className="Row">
        <AppCardTitle>{title}</AppCardTitle>
        <If condition={isDisabled}>
          <AppIcon icon={LOCK_SVG} fill="gray" size={16} />
        </If>
      </AppCardHeader>
      <AppCardContent>
        <div onClick={onClickCode} className="form-group">
          {diagnosis === null || diagnosis.formattedDxCode === undefined ? (
            <span>{translate(STRINGS.SELECT_DX_CODE)}</span>
          ) : (
            <div className="__icd-value">
              <span className="__code">{diagnosis.formattedDxCode}</span>
              <span className="__description">{diagnosis.shortDesc}</span>
            </div>
          )}
        </div>

        <div
          onClick={onClickStartDate}
          className={classNames("form-group", {
            __selected: fmap(diagnosis, (x) => x.startDate) !== null,
            __disabled: withDefault(isDisabled, fmap(diagnosis, (x) => x.icdCode) === null),
          })}
        >
          {diagnosis === null || diagnosis.startDate === null ? (
            <span>
              {isDisabled ? translate(STRINGS.DATE_NOT_SPECIFIED) : translate(STRINGS.START_DATE)}
            </span>
          ) : (
            <span className="__selected">
              {globalize().formatLocalDate(diagnosis.startDate, { date: "short" })}
            </span>
          )}
          <div className="__end">
            <AppIcon size={24} className="__icon" icon={CALENDAR_SVG} />
          </div>
        </div>

        <div
          onClick={onClickEndDate}
          className={classNames("form-group", {
            __selected: fmap(diagnosis, (x) => x.endDate) !== null,
            __disabled: withDefault(isDisabled, fmap(diagnosis, (x) => x.icdCode) === null),
          })}
        >
          {diagnosis === null || diagnosis.endDate === null ? (
            <span>
              {isDisabled ? translate(STRINGS.DATE_NOT_SPECIFIED) : translate(STRINGS.END_DATE)}
            </span>
          ) : (
            <span className="__selected">
              {globalize().formatLocalDate(diagnosis.endDate, { date: "short" })}
            </span>
          )}
          <div className="__end">
            <AppIcon size={24} className="__icon" icon={CALENDAR_SVG} />
          </div>
        </div>
      </AppCardContent>
      <If
        condition={!isDisabled && (diagnosis !== null || draft !== null)}
        comment="!isDisabled && (diagnosis !== null || draft !== null)"
      >
        <AppCardFooter>
          <If condition={isLoading}>
            <CardButton onClick={onClickDelete} color="danger" variation="outline">
              <Ons.ProgressCircular indeterminate={true} style={{ transform: "scale(0.65)" }} />
            </CardButton>
          </If>

          <If condition={!isLoading}>
            <Flex className="ICDCardControls" direction="column">
              <Flex className="UpdatePatientProfileControls" justifyContent="space-between">
                <span className="leading">Update to patient profile</span>
                <AppSwitch
                  checked={isProfileSync}
                  onChange={() => onClickProfileSync(originalDiagnosis)}
                />
              </Flex>

              <Flex justifyContent="flex-end">
                <IsDefined value={originalDiagnosis}>
                  {() => (
                    <CardButton onClick={onClickDelete} color="danger" variation="outline">
                      <AppIcon icon={TIMES_SVG} stroke="var(--warning)" size={12} />
                      <span>{translate(STRINGS.DELETE)}</span>
                    </CardButton>
                  )}
                </IsDefined>

                <IsDefined value={draft}>
                  {() => (
                    <>
                      <CardButton onClick={onClickCancel} variation="outline">
                        <span>{translate(STRINGS.CANCEL)}</span>
                      </CardButton>

                      <CardButton onClick={onClickSave} variation="filled">
                        <AppIcon icon={CHECK_SVG} stroke="white" size={12} />
                        <span>{translate(STRINGS.SAVE)}</span>
                      </CardButton>
                    </>
                  )}
                </IsDefined>
              </Flex>
            </Flex>
          </If>
        </AppCardFooter>
      </If>
    </AppCard>
  );
};

export default React.memo(ICDCard);
